class CustomShopByMoodCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-custom-shop-by-mood-track]');
    this.prevButton = this.querySelector('[data-custom-shop-by-mood-prev]');
    this.nextButton = this.querySelector('[data-custom-shop-by-mood-next]');

    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.count = this.slides.length;
    this.realCount = Number(this.dataset.count) || this.count;
    this.activeIndex = Math.min(2, this.count - 1);
    this.dragStartX = null;
    this.dragDeltaX = 0;
    this.visibleTiers = 2;

    if (this.count === 0) return;

    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onTrackClick = this.onTrackClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.track.addEventListener('keydown', this.onKeydown);
    this.track.addEventListener('pointerdown', this.onPointerDown);

    // Delegated on purpose: under `perspective` + `preserve-3d`, browsers can
    // hit-test a click on a rotated slide back to this <ul> instead of the
    // <li>/<a> underneath it. A listener on the slide itself would never see
    // that event (bubbling only goes up from the real target, never back
    // down into descendants), so the listener has to live here, with the
    // actual slide resolved from the click afterward.
    this.track.addEventListener('click', this.onTrackClick);

    if (window.Shopify && window.Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    this.render();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.track?.removeEventListener('keydown', this.onKeydown);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('click', this.onTrackClick);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Shortest signed distance from activeIndex to index around the ring.
  // Positioning every slide from this value (rather than its physical DOM
  // position) keeps the coverflow correct no matter how the loop-padding
  // clones are arranged, and no matter which direction you navigate.
  shortestOffset(index) {
    const half = Math.floor(this.count / 2);
    let raw = index - this.activeIndex;

    if (raw > half) raw -= this.count;
    if (raw < -half) raw += this.count;

    return raw;
  }

  render() {
    this.slides.forEach((slide, index) => {
      const offset = this.shortestOffset(index);
      const absOffset = Math.abs(offset);
      const isActive = offset === 0;
      const isVisible = absOffset <= this.visibleTiers;

      // Always position from the continuous, unclamped offset (never reset
      // to a "hidden at center" default). That's what keeps a slide's
      // entrance/exit a smooth rotation through the off-stage buffer
      // instead of a jump from wherever it last happened to rest.
      slide.style.transform =
        'translate(-50%, 0) ' +
        `translateX(${offset * 100}%) ` +
        `translateZ(calc(var(--csbm-depth-step) * ${absOffset})) ` +
        `rotateY(${offset * -15}deg)`;
      slide.style.zIndex = String(1 - absOffset);
      slide.style.opacity = isVisible ? '1' : '0';
      slide.style.pointerEvents = isVisible ? 'auto' : 'none';

      slide.dataset.active = String(isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    if (this.prevButton) this.prevButton.disabled = this.realCount < 2;
    if (this.nextButton) this.nextButton.disabled = this.realCount < 2;
  }

  goTo(index) {
    const normalized = ((index % this.count) + this.count) % this.count;
    this.activeIndex = normalized;
    this.render();
  }

  onPrevClick() {
    this.goTo(this.activeIndex - 1);
  }

  onNextClick() {
    this.goTo(this.activeIndex + 1);
  }

  resolveSlideFromEvent(event) {
    const direct = event.target.closest('.custom-shop-by-mood__slide');
    if (direct) return direct;

    // Fallback for the rotated-slide hit-test quirk: event.target landed on
    // the track itself, so ask the browser what's actually stacked at these
    // screen coordinates instead.
    if (typeof document.elementsFromPoint !== 'function') return null;

    const stack = document.elementsFromPoint(event.clientX, event.clientY);

    for (const element of stack) {
      const slide = element.closest?.('.custom-shop-by-mood__slide');
      if (slide) return slide;
    }

    return null;
  }

  onTrackClick(event) {
    try {
      const slide = this.resolveSlideFromEvent(event);
      if (!slide) return;

      const index = Number(slide.dataset.index);

      if (index !== this.activeIndex) {
        event.preventDefault();
        this.goTo(index);
      }
    } catch (error) {
      console.error('[custom-shop-by-mood] onTrackClick failed:', error);
    }
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.onPrevClick();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.onNextClick();
    }
  }

  onPointerDown(event) {
    if (this.realCount < 2) return;

    this.dragStartX = event.clientX;
    this.dragDeltaX = 0;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp, { once: true });
  }

  onPointerMove(event) {
    if (this.dragStartX === null) return;
    this.dragDeltaX = event.clientX - this.dragStartX;
  }

  onPointerUp() {
    window.removeEventListener('pointermove', this.onPointerMove);

    const threshold = 40;

    if (this.dragDeltaX > threshold) {
      this.onPrevClick();
    } else if (this.dragDeltaX < -threshold) {
      this.onNextClick();
    }

    this.dragStartX = null;
    this.dragDeltaX = 0;
  }

  onBlockSelect(event) {
    const slide = this.slides.find(
      (item) => item.dataset.blockId === event.detail.blockId
    );

    if (slide) {
      this.goTo(Number(slide.dataset.index));
    }
  }
}

if (!customElements.get('custom-shop-by-mood-carousel')) {
  customElements.define('custom-shop-by-mood-carousel', CustomShopByMoodCarousel);
}
